import { action, computed, makeObservable, observable } from 'mobx'
import { EventEmitter } from '../../util/EventEmitter'
import { ServerIdentities } from '../../types'
import type { MeetingCubit } from '../MeetingCubit'
import { DisposerTracker } from '../../util/DisposerTracker'
import { roomStateUpdatePumpSlideWithActiveSlideChangedAt } from '../../firestore/RoomState'

export type SecretaryConnectionState =
  | 'initializing'
  | 'connected'
  | 'disconnected'

export type SecretaryConnectionEvents = {
  connected: []
  disconnected: []
  reconnected: []
  timeout: []
}

export class SecretaryConnectionController {
  @observable
  state: SecretaryConnectionState = 'initializing'

  private disposers = new DisposerTracker()

  private reconnectInterval: ReturnType<typeof setInterval> | null = null
  private initTimeout: ReturnType<typeof setTimeout> | null = null

  events = new EventEmitter<SecretaryConnectionEvents>()

  constructor(private meeting: MeetingCubit) {
    makeObservable(this)
  }

  initialize() {
    this.startInitTimeout()
  }

  @computed
  get doesNotNeedSecretary(): boolean {
    return this.meeting.isPreview || this.meeting.isDemo
  }

  @computed
  get isSecretaryMissing(): boolean {
    if (this.doesNotNeedSecretary) return false
    return this.state !== 'connected'
  }

  @computed
  get isSecretaryConnected(): boolean {
    if (this.doesNotNeedSecretary) return true
    return this.state === 'connected'
  }

  @computed
  get isSecretaryDisconnected(): boolean {
    if (this.doesNotNeedSecretary) return false
    return this.state === 'disconnected'
  }

  @action
  handleParticipantIdsUpdate(participantIds: string[]) {
    if (this.doesNotNeedSecretary) return

    const secretaryPresent = participantIds.includes(ServerIdentities.Secretary)

    if (secretaryPresent) {
      if (this.state === 'initializing') {
        this.state = 'connected'
        this.stopInitTimeout()
        this.meeting.logEvent('meeting_secretary_connected')
        this.events.emit('connected')
      } else if (this.state === 'disconnected') {
        this.state = 'connected'
        this.meeting.logEvent('meeting_secretary_connected')
        this.stopReconnectInterval()
        this.events.emit('reconnected')
      }
    } else {
      if (this.state === 'connected') {
        this.state = 'disconnected'
        this.meeting.logEvent('meeting_secretary_disconnected')
        this.startReconnectInterval()
        this.events.emit('disconnected')
      }
    }
  }

  private startInitTimeout() {
    if (this.doesNotNeedSecretary) return
    if (this.state !== 'initializing') return
    if (this.initTimeout) return

    this.initTimeout = setTimeout(() => {
      if (this.state !== 'initializing') return
      this.handleInitTimeout()
    }, 60_000)
  }

  @action
  private handleInitTimeout() {
    this.state = 'disconnected'
    this.meeting.logEvent('meeting_secretary_timeout')
    this.startReconnectInterval()
    this.events.emit('timeout')
  }

  private stopInitTimeout() {
    if (this.initTimeout) {
      clearTimeout(this.initTimeout)
      this.initTimeout = null
    }
  }

  private startReconnectInterval() {
    if (this.meeting.isPreview || this.meeting.isDemo) return
    if (!this.meeting.currentUserIsGroupLeader) return
    if (this.reconnectInterval) return

    this.reconnectInterval = setInterval(() => {
      if (this.state === 'connected') {
        this.stopReconnectInterval()
        return
      }
      this.pumpSlideWithActiveSlideTouch()
    }, 15_000)
  }

  private stopReconnectInterval() {
    if (this.reconnectInterval) {
      clearInterval(this.reconnectInterval)
      this.reconnectInterval = null
    }
  }

  private pumpSlideWithActiveSlideTouch() {
    if (!this.meeting.currentUserIsGroupLeader) return
    this.meeting.logEvent('pump_slide_with_active_slide_touch', {
      slide_index: this.meeting.activeSlide,
    })
    roomStateUpdatePumpSlideWithActiveSlideChangedAt(this.meeting.firestore, {
      roomId: this.meeting.roomId,
    }).catch(() => {
      // Silently ignore — the interval will retry
    })
  }

  dispose() {
    this.stopInitTimeout()
    this.stopReconnectInterval()
    this.disposers.dispose()
  }
}
